/**
 * Pixel's "sign board" mode: while he flies around during the quest he doesn't send chat
 * messages — he shows an icon and a scrolling word on his LED face instead, like a little
 * departure board. Drawn on the same 26×26 dot grid as his face.
 */
export type GlyphIcon = 'up' | 'down' | 'here' | 'slider' | 'none';

export interface FaceGlyph {
  icon: GlyphIcon;
  /** Short, upper-case message that scrolls across the lower half of his face */
  text: string;
  /** slider: cursor position 0..1 */
  value?: number;
  /** slider: width of the target zone in the middle (0..1) */
  zone?: number;
  /** slider: which way the cursor is moving (1 right, -1 left) — for its trail */
  dir?: number;
  /** slider: hits landed / hits needed (progress pips) */
  hits?: number;
  need?: number;
  /** slider: feedback after a click, and how far it has played (0..1) */
  flash?: 'hit' | 'miss' | null;
  flashAge?: number;
  /** Text stands still (centred) instead of scrolling */
  still?: boolean;
}

/** 3×5 dot font (rows top → bottom) */
const FONT: Record<string, string[]> = {
  A: ['010', '101', '111', '101', '101'],
  B: ['110', '101', '110', '101', '110'],
  C: ['011', '100', '100', '100', '011'],
  D: ['110', '101', '101', '101', '110'],
  E: ['111', '100', '110', '100', '111'],
  F: ['111', '100', '110', '100', '100'],
  G: ['011', '100', '101', '101', '011'],
  H: ['101', '101', '111', '101', '101'],
  I: ['111', '010', '010', '010', '111'],
  J: ['001', '001', '001', '101', '010'],
  K: ['101', '101', '110', '101', '101'],
  L: ['100', '100', '100', '100', '111'],
  M: ['101', '111', '111', '101', '101'],
  N: ['110', '101', '101', '101', '101'],
  O: ['010', '101', '101', '101', '010'],
  P: ['110', '101', '110', '100', '100'],
  Q: ['010', '101', '101', '110', '011'],
  R: ['110', '101', '110', '101', '101'],
  S: ['011', '100', '010', '001', '110'],
  T: ['111', '010', '010', '010', '010'],
  U: ['101', '101', '101', '101', '111'],
  V: ['101', '101', '101', '101', '010'],
  W: ['101', '101', '111', '111', '101'],
  X: ['101', '101', '010', '101', '101'],
  Y: ['101', '101', '010', '010', '010'],
  Z: ['111', '001', '010', '100', '111'],
  '1': ['010', '110', '010', '010', '111'],
  '2': ['110', '001', '010', '100', '111'],
  '3': ['110', '001', '010', '001', '110'],
  '!': ['010', '010', '010', '000', '010'],
  '?': ['110', '001', '010', '000', '010'],
  '.': ['000', '000', '000', '000', '010'],
  ' ': ['000', '000', '000', '000', '000'],
};
/** Each character takes 3 dots + 1 gap */
const ADV = 4;
/** First row of the 5-row text band */
const TEXT_ROW = 15;
/** Scroll speed (dots per second) — slow enough to read while he flies */
const SCROLL = 6.5;

/** Is this dot of the icon lit? (face space: -1..1, y up; the icon lives in the upper half) */
function iconLit(icon: GlyphIcon, x: number, y: number, time: number): boolean {
  if (icon === 'none') return false;
  const b = 0.06 * Math.sin(time * 5);
  if (icon === 'up') {
    const tip = 0.86 + b;
    const base = 0.38 + b;
    if (y <= tip && y >= base && Math.abs(x) <= (tip - y) * 0.85) return true;
    return Math.abs(x) < 0.11 && y < base + 0.02 && y > 0.06 + b;
  }
  if (icon === 'down') {
    const tip = 0.06 + b;
    const base = 0.54 + b;
    if (y >= tip && y <= base && Math.abs(x) <= (y - tip) * 0.85) return true;
    return Math.abs(x) < 0.11 && y > base - 0.02 && y < 0.86 + b;
  }
  if (icon === 'here') {
    // A blinking "!"
    if (Math.sin(time * 7) < -0.55) return false;
    if (Math.abs(x) < 0.1 && y > 0.32 && y < 0.86) return true;
    return Math.abs(x) < 0.1 && y > 0.08 && y < 0.2;
  }
  return false;
}

/** Is dot (i, j) of the grid lit by the scrolling (or still, centred) text? */
function textLit(text: string, i: number, j: number, grid: number, time: number, still = false): boolean {
  const row = j - TEXT_ROW;
  if (row < 0 || row > 4 || !text) return false;
  const width = text.length * ADV;
  let col: number;
  if (still) {
    col = i - Math.floor((grid - (width - 1)) / 2);
  } else {
    // Enters from the right edge, runs out the left, loops
    const total = width + grid;
    const pos = Math.floor(time * SCROLL) % total;
    col = i + pos - grid;
  }
  if (col < 0 || col >= width) return false;
  const ch = FONT[text[Math.floor(col / ADV)]] ?? FONT[' '];
  const c = col % ADV;
  return c < 3 && ch[row][c] === '1';
}

/**
 * Timing game: a cursor glides left ↔ right (easing into each turn, with a short motion trail) along
 * a wide dotted track. Two posts mark the target zone in the middle; they stretch up while the
 * cursor is inside it. Progress pips above count the hits. A hit makes the zone blink full; a miss
 * shakes it.
 */
function sliderLit(g: FaceGlyph, x: number, y: number, half: number): boolean {
  const zone = g.zone ?? 0.2;
  const span = 0.86; // usable half-width of the track — nearly the whole face
  const edge = zone * span;
  const cx = ((g.value ?? 0.5) * 2 - 1) * span;
  const inZone = Math.abs(cx) <= edge;
  const age = Math.min(1, g.flashAge ?? 1);
  const shake = g.flash === 'miss' && age < 1 ? Math.sin(age * 55) * 0.07 * (1 - age) : 0;
  const xx = x - shake;
  const trackY = 0.36;

  // Progress pips: one per hit needed, lit as you land them
  const need = g.need ?? 4;
  if (y > 0.74 && y < 0.84) {
    for (let k = 0; k < need; k++) {
      const px = (k - (need - 1) / 2) * 0.24;
      if (Math.abs(x - px) < half * 1.3) return k < (g.hits ?? 0);
    }
    return false;
  }
  // Hit: the whole zone blinks full
  if (g.flash === 'hit' && age < 0.7 && Math.abs(xx) < edge && Math.abs(y - trackY) < 0.13) {
    return Math.floor(age * 12) % 2 === 0;
  }
  // Cursor: a tall bar
  if (Math.abs(xx - cx) < half * 1.6 && y > 0.16 && y < 0.58) return true;
  // Trail: shrinking ticks behind it, in the direction it came from
  const dir = g.dir ?? 1;
  for (let t = 1; t <= 3; t++) {
    const tx = cx - dir * t * half * 2.4;
    if (Math.abs(xx - tx) < half * 0.9 && Math.abs(y - trackY) < 0.14 - t * 0.035) return true;
  }
  // Posts: stretch up while the cursor is between them
  const postH = inZone ? 0.3 : 0.18;
  if (Math.abs(Math.abs(xx) - edge - half * 1.6) < half && Math.abs(y - trackY) < postH) return true;
  // Track: solid inside the zone, dotted outside
  if (Math.abs(y - trackY) < half && Math.abs(xx) < span + 0.06) {
    return Math.abs(xx) < edge || Math.round((xx + 1) / (2 * half)) % 2 === 0;
  }
  return false;
}

/** Lit dot of the sign board? */
export function glyphLit(glyph: FaceGlyph, i: number, j: number, grid: number, x: number, y: number, time: number) {
  const half = 1 / grid;
  const icon = glyph.icon === 'slider' ? sliderLit(glyph, x, y, half) : iconLit(glyph.icon, x, y, time);
  return icon || textLit(glyph.text, i, j, grid, time, glyph.still);
}
